/**
 * Hand-written DPP supplement to the auto-generated client.
 *
 * The auto-generator (openapi-ts) builds against the live `/openapi.json`,
 * but the currently-deployed tapdpp backend exposes a slightly different
 * route set than the legacy frontend expects (e.g. `recoverPassword`
 * routes are missing). Regenerating wholesale removes those methods and
 * breaks pre-existing callers. To avoid that churn while still getting
 * type-safe access to the DPP endpoints, this file ships only the DPP
 * types and `PassportTemplatesService` class. It can be deleted once the
 * legacy callers are reconciled with the live openapi spec.
 */
import type { CancelablePromise } from "./core/CancelablePromise"
import { OpenAPI } from "./core/OpenAPI"
import { request as __request } from "./core/request"

// ---------------------------------------------------------------------------
// Types — mirror backend `app/models/dpp.py` exactly.
// ---------------------------------------------------------------------------
export type DppSectionTypeEnum =
  | "HERO"
  | "IDENTITY"
  | "SPECS"
  | "LOCATION"
  | "PROVENANCE"
  | "DOCUMENTS"
  | "MATERIALS"
  | "CONTACT"
  | "QR"
  | "PRODUCT"
  | "MANUFACTURER"
  | "REPRESENTATIVE"
  | "SCAN_LOCATION"
  | "RFID"
  | "SUSTAINABILITY"
  | "CUSTOM_HTML"
  | "CUSTOM_JSON_LD"

export type DppExposableTagField =
  | "name"
  | "epcid"
  | "description"
  | "category"
  | "serial_number"
  | "sku"
  | "images"
  | "location_building"
  | "location_floor"
  | "location_zone"
  | "location_room"
  | "assigned_to"
  | "acquisition_date"
  | "asset_status"
  | "asset_value"
  | "notes"
  | "last_seen_at"
  | "last_lat"
  | "last_lon"
  | "chip_type"
  | "tag_type"

export type DppSectionConfig = {
  type: DppSectionTypeEnum
  visible?: boolean
  title_i18n?: Record<string, string>
  fields?: DppExposableTagField[]
  body_i18n?: Record<string, string>
  extra?: Record<string, unknown>
}

export type PassportTemplateBase = {
  organisation_id: string
  name: string
  enabled?: boolean
  is_default?: boolean
  logo_url?: string | null
  primary_color?: string | null
  accent_color?: string | null
  font_family?: string | null
  sections?: DppSectionConfig[]
  visible_fields?: string[]
  auto_bind_tag_type?: string | null
  auto_bind_chip_manufacturer?: string | null
  auto_bind_cin_brand?: string | null
  auto_bind_nfc_ic_manufacturer?: string | null
}

export type PassportTemplateCreate = PassportTemplateBase
export type PassportTemplateUpdate = Partial<PassportTemplateBase> & {
  name?: string
}
export type PassportTemplateRead = PassportTemplateBase & {
  id: string
}

export type PassportTemplatesPublic = {
  data: PassportTemplateRead[]
  count: number
}

export type DppRenderedSection = {
  type: DppSectionTypeEnum
  title?: string | null
  data?: Record<string, unknown>
}

export type DppPublicTagPreview = {
  id: string
  epcid: string
  name?: string | null
  chip_type?: string | null
  tag_type?: string | null
  last_seen_at?: string | null
}

export type DppPublicAvailableTemplate = {
  id: string
  name: string
  is_default?: boolean
}

export type DppPublic = {
  identifier: string
  /** "ok" — passport rendered. "unbound" — tag exists but no template bound. */
  status?: "ok" | "unbound"
  organisation_id?: string | null
  organisation_name: string
  branding?: Record<string, string | null>
  sections?: DppRenderedSection[]
  json_ld?: Record<string, unknown> | null
  is_demo?: boolean
  tag_preview?: DppPublicTagPreview | null
  available_templates?: DppPublicAvailableTemplate[]
}

export type TagPassportBindingUpdate = {
  template_id?: string | null
  locked?: boolean
}

// ---------------------------------------------------------------------------
// Request shapes — mirror @hey-api/openapi-ts conventions (camelCased
// path params + a `requestBody` slot for POST/PUT bodies).
// ---------------------------------------------------------------------------
export type ListPassportTemplatesData = {
  organisationId: string
  skip?: number
  limit?: number
}

export type CreatePassportTemplateData = {
  organisationId: string
  requestBody: PassportTemplateCreate
}

export type ReadPassportTemplateData = {
  organisationId: string
  templateId: string
}

export type UpdatePassportTemplateData = {
  organisationId: string
  templateId: string
  requestBody: PassportTemplateUpdate
}

export type DeletePassportTemplateData = {
  organisationId: string
  templateId: string
}

export type SetTagPassportBindingData = {
  organisationId: string
  tagId: string
  requestBody: TagPassportBindingUpdate
}

export type ResolvePublicPassportData = {
  identifier: string
}

// ---------------------------------------------------------------------------
// Service classes
// ---------------------------------------------------------------------------
export class PassportTemplatesService {
  /** List passport templates for an organisation. */
  public static listPassportTemplates(
    data: ListPassportTemplatesData,
  ): CancelablePromise<PassportTemplatesPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/organizations/{organisation_id}/passport-templates/",
      path: { organisation_id: data.organisationId },
      query: { skip: data.skip, limit: data.limit },
      errors: { 422: "Validation Error" },
    })
  }

  /** Create a passport template scoped to the path's organisation. */
  public static createPassportTemplate(
    data: CreatePassportTemplateData,
  ): CancelablePromise<PassportTemplateRead> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/organizations/{organisation_id}/passport-templates/",
      path: { organisation_id: data.organisationId },
      body: data.requestBody,
      mediaType: "application/json",
      errors: { 422: "Validation Error" },
    })
  }

  /** Get a single passport template. */
  public static readPassportTemplate(
    data: ReadPassportTemplateData,
  ): CancelablePromise<PassportTemplateRead> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/organizations/{organisation_id}/passport-templates/{template_id}",
      path: {
        organisation_id: data.organisationId,
        template_id: data.templateId,
      },
      errors: { 422: "Validation Error" },
    })
  }

  /** Patch fields on an existing passport template. */
  public static updatePassportTemplate(
    data: UpdatePassportTemplateData,
  ): CancelablePromise<PassportTemplateRead> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/api/v1/organizations/{organisation_id}/passport-templates/{template_id}",
      path: {
        organisation_id: data.organisationId,
        template_id: data.templateId,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: { 422: "Validation Error" },
    })
  }

  /** Delete a passport template. */
  public static deletePassportTemplate(
    data: DeletePassportTemplateData,
  ): CancelablePromise<{ message: string }> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/api/v1/organizations/{organisation_id}/passport-templates/{template_id}",
      path: {
        organisation_id: data.organisationId,
        template_id: data.templateId,
      },
      errors: { 422: "Validation Error" },
    })
  }
}

export class TagPassportBindingsService {
  /** Bind / unbind / lock a tag's public passport template. */
  public static setTagPassportBinding(
    data: SetTagPassportBindingData,
  ): CancelablePromise<unknown> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/organizations/{organisation_id}/tags/{tag_id}/passport-binding",
      path: {
        organisation_id: data.organisationId,
        tag_id: data.tagId,
      },
      body: data.requestBody,
      mediaType: "application/json",
      errors: { 422: "Validation Error" },
    })
  }
}

export class DppService {
  /** Resolve a public Digital Product Passport by identifier (no auth). */
  public static resolvePublicPassport(
    data: ResolvePublicPassportData,
  ): CancelablePromise<DppPublic> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/dpp/{identifier}",
      path: { identifier: data.identifier },
      errors: {
        404: "No matching public passport for this identifier",
        422: "Validation Error",
        429: "Rate limit exceeded",
      },
    })
  }

  /** Public bind — visitor selects a template for an unbound tag (demo only). */
  public static publicBindTagTemplate(data: {
    identifier: string
    requestBody: { template_id: string }
  }): CancelablePromise<DppPublic> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/api/v1/dpp/{identifier}/bind-template",
      path: { identifier: data.identifier },
      body: data.requestBody,
      mediaType: "application/json",
      errors: {
        404: "Disabled outside demo mode or no match found",
        422: "Validation Error",
        429: "Rate limit exceeded",
      },
    })
  }

  /** Cluster info (auth-gated): mqtt host + derived dpp host + flags. */
  public static getDppClusterInfo(data: {
    organisationId: string
  }): CancelablePromise<{
    mqtt_host: string
    derived_dpp_host: string | null
    dpp_public_enabled: boolean
    dpp_rate_limit: string
  }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/api/v1/organizations/{organisation_id}/passport-templates/_cluster-info",
      path: { organisation_id: data.organisationId },
      errors: { 403: "Forbidden", 422: "Validation Error" },
    })
  }
}
